import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Row,
  Col,
  Skeleton,
  Form,
  Checkbox,
  Typography,
  Input,
  notification,
} from 'antd';

import { useDefaultFormErrorNotification, useWindowSize } from '@/hooks';

import {
  UpdateWorkordersConfigurationProfile,
  WorkorderColumn,
} from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import {
  useUpdateWorkorderConfigurationProfile,
  useWorkorderConfigurationProfile,
} from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';

import { PageProps } from '@/types/PageProps';
import { SaveButton } from '@/components/_common/Buttons';
import { useMemo } from 'react';
import { isAppPortocar } from '@/index';

const { Title, Paragraph } = Typography;

function WorkorderConfigurationPage({ route }: PageProps) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<UpdateWorkordersConfigurationProfile>();
  const { width } = useWindowSize();

  const { data, isFetching } = useWorkorderConfigurationProfile();

  const dataFields = useMemo(() => {
    if (!isAppPortocar) {
      return (
        data?.data.filter(
          (field) =>
            field.column !== WorkorderColumn.PortoBattery &&
            field.column !== WorkorderColumn.PortoBatteryWarranty,
        ) || []
      );
    }
    return data?.data || [];
  }, [data?.data]);

  const mutation = useUpdateWorkorderConfigurationProfile();

  const submit = async () => {
    try {
      const values = await form.validateFields();

      await mutation.mutateAsync(values);

      notification.success({
        message: intl.formatMessage({
          id: 'settings.workorderConfiguration.update.success',
        }),
        description: intl.formatMessage({
          id: 'settings.workorderConfiguration.update.success.description',
        }),
      });
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  return (
    <div className="page">
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page-header">
        <Title level={4}>
          <FormattedMessage id={route.name} />
        </Title>

        {!(width < 992) && (
          <SaveButton
            type="primary"
            onClick={submit}
            loading={mutation.isLoading}
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        )}
      </div>

      <Paragraph style={{ marginTop: '16px' }}>
        <FormattedMessage id="settings.workorderConfiguration.description" />
      </Paragraph>

      <Skeleton active loading={isFetching}>
        <Form form={form} style={{ marginTop: '24px' }}>
          <Row gutter={[16, 12]}>
            {dataFields.map((item, i) => (
              <Col span={24} lg={12} key={item.column}>
                <Form.Item
                  hidden
                  name={['configurations', i, 'column']}
                  initialValue={item.column}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  noStyle
                  name={['configurations', i, 'active']}
                  initialValue={item.active}
                  valuePropName="checked"
                >
                  <Checkbox>
                    <FormattedMessage
                      id={`settings.workorderConfiguration.${item.column}`}
                    />
                  </Checkbox>
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      </Skeleton>

      {width < 992 && (
        <SaveButton
          block
          type="primary"
          onClick={submit}
          loading={mutation.isLoading}
          style={{ marginTop: '20px' }}
        >
          <FormattedMessage id="commons.entity.save" />
        </SaveButton>
      )}
    </div>
  );
}

export default WorkorderConfigurationPage;
